import cookies from 'js-cookie'
import root from 'window-or-global'

import { safeGet } from '@src/authorisation/helpers'
import Snowplow from '@src/snowplow'
import SSOScriptLoader from '@src/sso_scripts_loader'

import {
  NOT_FOUND_ERROR,
  FACEBOOK_SIGNED_REQUEST_COOKIE_NAME,
  POP_UP_CLOSED_BY_USER,
  NEW_SESSION_FROM_USER,
  APPLE_SSO_CLOSED_BY_USER
} from './constants'

export function isFacebookLoading(state) {
  return state.ssoInProgress === 'Facebook'
}

export function isGoogleLoading(state) {
  return state.ssoInProgress === 'Google'
}

export function canMakeFacebookSSORequest(props) {
  return SSOScriptLoader.hasFacebookScriptLoaded()
}

export function canMakeGoogleSSORequest(props) {
  return SSOScriptLoader.hasGoogleScriptLoaded()
}

export function canMakeAppleSSORequest(props) {
  return SSOScriptLoader.hasAppleScriptLoaded()
}

export function isFacebookResponseValid(response) {
  return !!response.authResponse
}

export function isGoogleResponseValid(response) {
  return !response.error
}

export function isAppleResponseValid(response) {
  return !!response.authorization
}

export function getGoogleAuthParams() {
  return {
    client_id: getGoogleClientId(),
    scope: 'email profile',
    ux_mode: 'popup'
  }
}

export function configureAppleAuth() {
  root.AppleID.auth.init({
    clientId: getAppleAuthValues('appleClientId'),
    scope: getAppleAuthValues('appleScope'),
    redirectURI: getAppleAuthValues('appleRedirectUri'),
    usePopup: true
  })
}

export function getResponse(data) {
  if (!data.user) return noUserData(data)

  return withUserData(data)
}

export function withUserData(data) {
  return {
    code: data.authorization.code,
    user: JSON.stringify(data.user),
    platform: 'js'
  }
}

export function noUserData(data) {
  return {
    code: data.authorization.code,
    platform: 'js'
  }
}

export function getAppleAuthValues(valueName) {
  return root.document
    .querySelector(`meta[name=${valueName}]`)
    .getAttribute('content')
}

export function responseHasNotFoundError(data) {
  return data.errors && NOT_FOUND_ERROR in data.errors
}

export function responseHasClosedError(data) {
  if (data.error) {
    if (data.error?.errors) {
      return data.error.errors[0].detail === APPLE_SSO_CLOSED_BY_USER
    }

    return (
      data.error === POP_UP_CLOSED_BY_USER ||
      data.error === NEW_SESSION_FROM_USER
    )
  }
}

export function getFacebookRoute(type) {
  return type === 'logIn' ? 'FACEBOOK_LOG_IN' : 'FACEBOOK_SIGN_UP'
}

export function getGoogleRoute(type) {
  return type === 'logIn' ? 'GOOGLE_LOG_IN' : 'GOOGLE_SIGN_UP'
}

export function trackSSOButtonClick(provider) {
  Snowplow.trackButtonClickEvent(
    {
      action: 'ssoClick'
    },
    [
      Snowplow.getButtonContext({
        text: provider,
        style: 'ssoButton'
      }),
      Snowplow.getButtonClickContext({
        action: 'ssoClick'
      })
    ]
  )
}

export function setFacebookSignedRequestCookie() {
  const authResponse = root.FB.getAuthResponse()
  if (!authResponse) return null

  cookies.set(
    getFacebookSignedRequestCookieName(),
    authResponse.signedRequest,
    {
      expires: getFacebookCookieExpiryTime(authResponse.expiresIn),
      sameSite: 'none',
      secure: true
    }
  )
}

export function getFacebookCookieExpiryTime(expiresInSeconds) {
  const expiresInAsMilliseconds = expiresInSeconds * 1000
  const now = new Date(Date.now()).getTime()
  return new Date(now + expiresInAsMilliseconds)
}

export function getFacebookAuthResponse() {
  return root.FB && root.FB.getAuthResponse()
}

export function getFacebookAppId() {
  return root._stb && root._stb.facebook_app_id
}

export function getGoogleClientId() {
  return root._stb && root._stb.google_client_id
}

export function getFacebookSignedRequestCookieName() {
  const appId = getFacebookAppId()
  return `${FACEBOOK_SIGNED_REQUEST_COOKIE_NAME}${appId}`
}

export function getRouterProps(props) {
  return {
    router: safeGet(['router'], props),
    params: safeGet(['match', 'params'], props),
    location: safeGet(['match', 'location'], props)
  }
}
